import React from 'react';

import messages from 'src/messages';
import { Message } from 'src/core/components';
import { routes as Routes } from 'src/constants';

// form fields --- plate size details
export const PLATE_SIZE_FIELD = 'plateSize';
export const WELL_FIELD = 'wellVolume';
export const SCALE_FIELD = 'synthesisScale';
export const PURIFICATION_FIELD = 'purification';
export const BUFFER_FIELD = 'buffer';
export const QUALITY_TESTING_FIELD = 'qualityTestingPercentage';
export const SHIPPING_FORMAT_FIELD = 'shipFormat';
export const DESIGN_ID = 'designId';
export const WELL_VOLUME_DEFAULT_VALUE_FOR_PLATE_SIZE_96 = 200;
export const WELL_VOLUME_DEFAULT_VALUE_FOR_PLATE_SIZE_384 = 120;

// form fields --- normalization/aliquots
export const NORMALIZATION = 'normalization';
export const NORMALIZATION_CONCENTRATION = 'concentrationNormalization';
export const NORMALIZATION_VOLUME = 'volumeNormalization';
export const NORMALIZATION_VOLUME_CONCENTRATION =
  'normalizationVolumeConcentration';
export const VOLUME = 'volume';
export const CONCENTRATION = 'concentration';
export const VOLUME_UNIT = 'volumeUnit';
export const CONCENTRATION_UNIT = 'concentrationUnit';
export const ALIQUOTS_CHECKED = 'aliquotsChecked';
export const ALIQUOTS_NUMBER = 'aliquotsNumber';
export const ALIQUOTS_VOLUME = 'aliquotsVolume';
export const NORMALIZATION_NONE = 'none';
export const REMAINDER_PLATE_TYPE = 'remainderPlateType';
export const REMAINDER_TREATMENT = 'remainderTreatment';
export const REMAINDER_VOLUME = 'remainderVolume';
export const NORMALIZATION_VOLUME_MIN_VALUE = 10;
export const NORMALIZATION_VOLUME_DEFAULT_VALUE = 15;
export const NORMALIZATION_CONCENTRATION_UM_MIN_VALUE = 1;
export const NORMALIZATION_CONCENTRATION_UM_MAX_VALUE = 250;
export const NORMALIZATION_CONCENTRATION_UG_UL_MIN_VALUE = 0.5;
export const NORMALIZATION_CONCENTRATION_UG_UL_MAX_VALUE = 10;
export const NORMALIZATION_CONCENTRATION_UM_DEFAULT_VALUE = 1;
export const ALIQUOTS_NUMBER_DEFAULT_VALUE = 1;
export const ALIQUOTS_NUMBER_MIN_VALUE = 1;
export const ALIQUOTS_NUMBER_MAX_VALUE = 99;
export const ALIQUOTS_VOLUME_DEFAULT_VALUE = 10;
export const ALIQUOTS_VOLUME_MIN_VALUE = 10;
export const REMAINDER_VOLUME_DEFAULT_VALUE = 0;
export const HUNDERED_PERCENT = 100;
export const EIGHTY_PERCENT = 80;
export const QUALITY_TESTING_DEFAULT_VALUE = 25;

// form fields --- pooled/plates
export const POOLED_PLATE_VOLUME_MIN_VALUE = 10;
export const POOLED_PLATE_VOLUME_MAX_VALUE = 12;
export const PLATE_POOLING = 'platePooling';
export const REMAINDER_CHECKED = 'hasRemainders';
export const POOLED_PLATE_VOLUME = 'volume';
export const POOLED_PLATES = 'pooledPlates';
export const PLATES_FOR_POOLING = 'platesForPooling';
export const POOLED_PLATE_NAME = 'pooledPlateName';
export const POOLED_PLATE_NAME_VALUE = 'Pooled Plate ';
export const POOL_LIST = 'poolList';

// namespaces
export const FILE_NAMESPACE = 'file';
export const PLATE_SIZE_DETAILS_NAMESPACE = 'plateSizeDetails';
export const NORMALIZATION_ALIQUOTS_NAMESPACE = 'normalizationAliquots';
export const PLATE_POOLING_NAMESPACE = 'platePooling';
export const PLATE_UPLOADED_NAMESPACE = 'plate_uploaded';
export const LOADER_NAMESPACE = 'is_loader_visible';
export const REMAINDERS_NAMESPACE = 'remainders';

export const PLATE_SIZE_DETAILS_NAMESPACE_ACTION = 'plate_size_details';
export const NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION = 'normalization_aliquots';
export const PLATE_POOLING_NAMESPACE_ACTION = 'plate_pooling';
export const STEPS_NAMESPACE_ACTION = 'dna_steps';
export const REMAINDERS_NAMESPACE_ACTION = 'remainders';

export const DNA_TYPE = 'DNAType';
export const DNA = 'DNA';
export const RNA = 'RNA';
export const AMPLISEQ = 'ampliseq';
export const AMPLISEQ_DNA = 'ampliseqDNA';
export const AMPLISEQ_RNA = 'ampliseqRNA';
export const SALES_QUOTE_REP_USER = 'Enterprise.SalesQuoteRep';
export const CUSTOMER_SERVICE_REPRESENTATIVE_USER =
  'Enterprise.CustomerServiceRepresentative';

// form data
export const UNITS = {
  volume: [{ label: 'uL', value: 'UL' }],
  concentration: [
    { label: 'uM', value: 'UM' },
    { label: 'ug/uL', value: 'UG_UL' },
  ],
};

export const ALL_UNITS = [...UNITS.volume, ...UNITS.concentration];

export const NORMALIZATION_OPTIONS = {
  NONE: 'NONE',
  VOLUME: 'VOLUME',
  CONCENTRATION: 'CONCENTRATION',
  CONCENTRATION_VOLUME: 'CONCENTRATION_VOLUME',
};

export const SMALL_WELL_OPTIONS = {
  SMALL: {
    type: 200,
    maxValue: 180,
  },
  EXTRA_SMALL: {
    type: 330,
    maxValue: 270,
  },
  MEDIUM: {
    type: 360,
    maxValue: 250,
  },
  LARGE: {
    type: 800,
    maxValue: 650,
  },
  EXTRA_LARGE: {
    type: 1000,
    maxValue: 750,
  },
  DOUBLE_EXTRA_LARGE: {
    type: 1200,
    maxValue: 800,
  },
  FULL_LARGE: {
    type: 1400,
    maxValue: 850,
  },
};

export const BIG_WELL_OPTIONS = {
  SMALL: {
    type: 65,
    maxValue: 45,
  },
  MEDIUM: {
    type: 120,
    maxValue: 75,
  },
};

export const PLATE_SIZES = {
  SIZE_96: 96,
  SIZE_384: 384,
};

// plate pooling
export const TREATMENTS = {
  DRY: 'DRY',
  FROZEN: 'FROZEN',
};

export const PLATE_POOLING_OPTIONS = {
  NONE: 'none',
  HALVES: 'firstHalfSecondHalf',
  ODD_EVEN: 'oddEven',
  CUSTOM: 'custom',
};

export const CLOSED_ANSWERS = {
  yes: 'yes',
  no: 'no',
};

export const POOLING = {
  NONE: 'none',
  FIRST_HALF_SECOND: 'fhsf',
  ODD_EVEN: 'Pooling odd/even',
  CUSTOM: 'Pooling custom',
};

export const BUFFER = {
  TRIS: 'TRIS',
  WATER: 'WATER',
  TE: 'TE',
};

export const PURIFICATION = {
  DESALTED: 'DESALTED',
  CARTRIDGE: 'CARTRIDGE',
  HPLC: 'HPLC',
  PAGE: 'PAGE',
};

export const SHIPPING_FORMAT = {
  FROZEN: 'FROZEN',
  DRIED: 'DRIED',
  AMBIENT: 'AMBIENT',
};

export const WELL_VOLUME_OPTIONS_96 = [
  {
    label: '200uL',
    value: 200,
  },
  {
    label: '360uL',
    value: 360,
  },
  {
    label: '800uL',
    value: 800,
  },
  {
    label: '1200uL',
    value: 1200,
  },
  {
    label: 'matrix II',
    value: 1400,
  },
  {
    label: 'matrix I',
    value: 1000,
  },
];

export const WELL_VOLUME_OPTIONS_384 = [
  {
    label: '65uL',
    value: 65,
  },
  {
    label: '120uL',
    value: 120,
  },
];

export const JAPAN_WELL_VOLUME_OPTIONS_96 = [
  {
    label: '330uL',
    value: 330,
  },
  {
    label: '800uL',
    value: 800,
  },
  {
    label: '1200uL',
    value: 1200,
  },
];

export const SECOND_PRIORITY_COUNTRY_OPTIONS_96 = [
  {
    label: '200uL',
    value: 200,
  },
  {
    label: '360uL',
    value: 360,
  },
  {
    label: '800uL',
    value: 800,
  },
  {
    label: '1200uL',
    value: 1200,
  },
];

export const ALL_WELL_VOL_OPTIONS = [
  ...WELL_VOLUME_OPTIONS_96,
  ...WELL_VOLUME_OPTIONS_384,
  ...JAPAN_WELL_VOLUME_OPTIONS_96,
];

export const SYNTHESIS_SCALE_OPTIONS = [
  {
    label: '25 nmole',
    value: 'N_25',
  },
  {
    label: '50 nmole',
    value: 'N_50',
  },
  {
    label: '200 nmole',
    value: 'N_200',
  },
  {
    label: '1 umole',
    value: 'U_1',
  },
  {
    label: '10 umole',
    value: 'U_10',
  },
];

export const JAPAN_SYNTHESIS_SCALE_OPTIONS = [
  {
    label: '25 nmole',
    value: 'N_25',
  },
  {
    label: '50 nmole',
    value: 'N_50',
  },
  {
    label: '200 nmole',
    value: 'N_200',
  },
];

export const PLATE_SIZE_OPTIONS = [
  {
    label: (
      <Message
        message={{ ...messages.wells, values: { totalOligos: '96-' } }}
      />
    ),
    value: 96,
  },
  {
    label: (
      <Message
        message={{ ...messages.wells, values: { totalOligos: '384-' } }}
      />
    ),
    value: 384,
  },
];

export const AMPLISEQ_DNA_PLATE_SIZE_OPTIONS = [
  {
    label: (
      <Message
        message={{
          ...messages.ampliseqDNAwells,
          values: { totalOligos: '96-' },
        }}
      />
    ),
    value: 96,
  },
  {
    label: (
      <Message
        message={{ ...messages.wells, values: { totalOligos: '384-' } }}
      />
    ),
    value: 384,
  },
];

export const AMPLISEQ_RNA_PLATE_SIZE_OPTIONS = [
  {
    label: (
      <Message
        message={{
          ...messages.ampliseqRNAwells,
          values: { totalOligos: '96-' },
        }}
      />
    ),
    value: 96,
  },
  {
    label: (
      <Message
        message={{ ...messages.wells, values: { totalOligos: '384-' } }}
      />
    ),
    value: 384,
  },
];

export const AMPLISEQ_PLATE_SIZE_OPTIONS = {
  [AMPLISEQ_DNA]: AMPLISEQ_DNA_PLATE_SIZE_OPTIONS,
  [AMPLISEQ_RNA]: AMPLISEQ_RNA_PLATE_SIZE_OPTIONS,
};

export const AMPLISEQ_PLATE_SIZE_OPTIONS_MSG = {
  [AMPLISEQ_DNA]: messages.ampliseqDNAwells,
  [AMPLISEQ_RNA]: messages.ampliseqRNAwells,
};

export const PURIFICATION_OPTIONS = [
  {
    label: <Message message={messages.desalted} />,
    value: PURIFICATION.DESALTED,
  },
  {
    label: <Message message={messages.cartridge} />,
    value: PURIFICATION.CARTRIDGE,
  },
  {
    label: <Message message={messages.hplc} />,
    value: PURIFICATION.HPLC,
  },
  {
    label: <Message message={messages.page} />,
    value: PURIFICATION.PAGE,
  },
];

export const SHIPPING_FORMAT_OPTIONS = [
  {
    label: <Message message={messages.frozen} />,
    value: SHIPPING_FORMAT.FROZEN,
  },
  {
    label: <Message message={messages.dried} />,
    value: SHIPPING_FORMAT.DRIED,
  },
  {
    label: <Message message={messages.ambient} />,
    value: SHIPPING_FORMAT.AMBIENT,
  },
];

export const JAPAN_SHIPPING_FORMAT_OPTIONS = [
  {
    label: <Message message={messages.dried} />,
    value: SHIPPING_FORMAT.DRIED,
  },
  {
    label: <Message message={messages.liquidTE} />,
    value: SHIPPING_FORMAT.AMBIENT,
  },
];

export const ALL_SHIPPING_FORMAT_OPTIONS = [
  ...SHIPPING_FORMAT_OPTIONS,
  ...JAPAN_SHIPPING_FORMAT_OPTIONS,
];

export const BUFFER_OPTIONS = [
  {
    label: <Message message={messages.tris} />,
    value: BUFFER.TRIS,
  },
  {
    label: <Message message={messages.water} />,
    value: BUFFER.WATER,
  },
  {
    label: <Message message={messages.te} />,
    value: BUFFER.TE,
  },
];

export const QUALITY_TESTING_OPTIONS = [
  {
    label: '100% QC',
    value: 100,
  },
];

export const REMAINDER_VALUES = {
  DRY: 'DRY',
  FROZEN: 'FROZEN',
  UL_200: 'UL_200',
  UL_1200: 'UL_1200',
};

export const REMAINDER_TREATMENT_OPTIONS = [
  {
    label: <Message message={messages.dry} />,
    value: REMAINDER_VALUES.DRY,
  },
  {
    label: <Message message={messages.frozen} />,
    value: REMAINDER_VALUES.FROZEN,
  },
];

export const REMAINDER_PLATE_TYPE_OPTIONS = [
  {
    label: <Message message={messages.wellPlate200} />,
    value: REMAINDER_VALUES.UL_200,
  },
  {
    label: <Message message={messages.wellPlate1200} />,
    value: REMAINDER_VALUES.UL_1200,
  },
];

export const DEFAULTS = {
  NORMALIZATION: NORMALIZATION_OPTIONS.NONE,
};

export const STEPS = {
  UPLOAD_FILE: 'UPLOAD_FILE',
  PLATE_SIZE_DETAILS: 'PLATE_SIZE_DETAILS',
  NORMALIZATION_ALIQUOTS: 'NORMALIZATION_ALIQUOTS',
  PLATE_POOLING: 'PLATE_POOLING',
  REVIEW_AND_SUBMIT: 'REVIEW_AND_SUBMIT',
};

// edit sequence constants
export const PRIMER_NAME = 'primerName';
export const OLIGO_SEQUENCE = 'oligoSequence';
export const MODIFICATION_5 = 'modification5';
export const MODIFICATION_3 = 'modification3';
export const sense5ModOptions = [
  { label: 'None', value: 'None' },
  { label: 'Aldehyde', value: 'ALD' },
  // { label: 'Alexa fluor 350', value: '350' },
  // { label: 'Alexa fluor 405', value: '405' },
  // { label: 'Alexa fluor 430', value: '430' },
  { label: 'Alexa fluor 488', value: '488' },
  // { label: 'Alexa fluor 514', value: '514' },
  { label: 'Alexa fluor 532', value: '532' },
  { label: 'Alexa fluor 546', value: '546' },
  { label: 'Alexa fluor 555', value: '555' },
  { label: 'Alexa fluor 568', value: '568' },
  { label: 'Alexa fluor 594', value: '594' },
  { label: 'Alexa fluor 633', value: '633' },
  { label: 'Alexa fluor 647', value: '647' },
  { label: 'Alexa fluor 660', value: '660' },
  { label: 'Alexa fluor 680', value: '680' },
  { label: 'Alexa fluor 700', value: '700' },
  { label: 'Alexa fluor 750', value: '750' },
  { label: 'Alkaline Phosphatase', value: 'ALP' },
  { label: 'Amine', value: 'AMN' },
  { label: 'Biotin', value: 'BIO' },
  { label: 'BODIPY® 493/503', value: 'BDC' },
  { label: 'BODIPY 530/550', value: 'BDB' },
  { label: 'BODIPY 550/560', value: 'BDP' },
  { label: 'BODIPY 558/569', value: 'BDE' },
  { label: 'BODIPY 564/570', value: 'BDF' },
  { label: 'BODIPY 576/589', value: 'BDG' },
  { label: 'BODIPY 581/591', value: 'BDH' },
  { label: 'BODIPY 630/650', value: 'BDN' },
  { label: 'BODIPY FL', value: 'BDA' },
  { label: 'BODIPY FL-X', value: 'BDI' },
  { label: 'BODIPY R6G', value: 'BDL' },
  { label: 'BODIPY R6G-X', value: 'BDM' },
  { label: 'BODIPY TMR', value: 'BDK' },
  { label: 'BODIPY TR-X', value: 'BDJ' },
  { label: 'FAM', value: 'FAM' },
  { label: 'Fluorescein', value: 'FLO' },
  { label: 'Gateway FW', value: 'GWF' },
  { label: 'Gateway RV', value: 'GWR' },
  { label: 'HEX', value: 'HEX' },
  { label: 'HorseRadish Peroxidase', value: 'HRP' },
  { label: 'JOE', value: 'JOE' },
  { label: 'Marina Blue™', value: 'MNB' },
  { label: 'Oregon Green 488', value: 'OGC' },
  { label: 'Oregon Green 488-X', value: 'OGD' },
  { label: 'Oregon Green 514', value: 'OGB' },
  { label: 'Pacific Blue™', value: 'PFB' },
  { label: 'Pacific Orange™', value: 'PFO' },
  { label: 'Phosphate', value: 'PHO' },
  { label: 'Rhodamine Green-X', value: 'RGC' },
  { label: 'Rhodamine Green™', value: 'RGA' },
  { label: 'Rhodamine RedÔ-X', value: 'RRA' },
  { label: 'Rhodamine', value: 'RHD' },
  { label: 'Rhodol Green', value: 'RGB' },
  { label: 'ROX', value: 'ROX' },
  { label: 'TAMRA', value: 'TAM' },
  { label: 'TET', value: 'TET' },
  { label: 'Texas Red®', value: 'TXR' },
  { label: 'Texas Red-X', value: 'TRX' },
  { label: 'Thiol', value: 'THL' },
];

export const sense3ModOptions = [
  { label: 'None', value: 'None' },
  { label: 'Biotin', value: 'BIO' },
  { label: 'Phosphate', value: 'PHO' },
  { label: 'Amine', value: 'AMN' },
];

// edit plate
export const PLATE_NAME = 'plateName';

// more info links
export const MORE_INFO_REMAINDERS_LINK =
  'https://www.thermofisher.com/configoligoremainders';
export const MORE_INFO_ALIQUOTS_LINK =
  'https://www.thermofisher.com/configoligoaliquot';

// country codes
export const COUNTRY_CODE_JAPAN = 'jp';

export const FILE_UPLOAD_ROUTES_CONFIG = {
  [AMPLISEQ_DNA]: Routes.AMPLISEQ_DNA,
  [AMPLISEQ_RNA]: Routes.AMPLISEQ_RNA,
};

export const COUNTRY_CODE_FRANCE = 'fr';
export const COUNTRY_CODE_GERMANY = 'de';
export const COUNTRY_CODE_RUSSIA = 'ru';
export const COUNTRY_CODE_SPAIN = 'es';
export const COUNTRY_CODE_PORTUGAL = 'pt';

// second priority countries
export const SECOND_PRIORITY_COUNTRIES = [
  COUNTRY_CODE_FRANCE,
  COUNTRY_CODE_GERMANY,
  COUNTRY_CODE_RUSSIA,
  COUNTRY_CODE_SPAIN,
  COUNTRY_CODE_PORTUGAL,
];
